// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Tax Profiles page (Stage 6P.9 — Phase 5B §L2).
//
// Operator surface over `supplier_tax_profiles` — the per-supplier
// PAN status / certificate / residency snapshot the TDS/TCS deduction
// engine reads at deduction time. The backend is the source of truth
// for amount-used tracking; this surface only authors snapshot rows.
//
//   GET    /api/erp/tds-tcs/supplier-profiles            (list)
//   POST   /api/erp/tds-tcs/supplier-profiles            (create/upsert)
//   GET    /api/erp/tds-tcs/supplier-profiles/:id        (read)
//   PUT    /api/erp/tds-tcs/supplier-profiles/:id        (update)
//
// Permissions (route-enforced; surface-gated here too):
//   erp.tds_tcs.profiles.view   — required to load this page
//   erp.tds_tcs.profiles.update — gates create + edit
//
// Brief §L2 columns:
//   Supplier · GSTIN · PAN · PAN Status · Resident Status
//   TDS Applicable · TCS Applicable · Certificate No · Certificate Rate
//   Certificate Validity · Exempt · Actions
//
// Mirrors style:
//   frontend/src/pages/erp/purchaseReturns/PurchaseReturnsPage.jsx
//   frontend/src/pages/erp/supplierPayments/SupplierPaymentRegisterPage.jsx
// Reuses the shared GoodsReceiptsPage.css via the .grn-* wrapper.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Dropdown, Form, Input, InputNumber,
  Modal, Select, Space, Spin, Switch, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined, PlusOutlined,
  EditOutlined, IdcardOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import client from '../../../api/client';
import RoleGate from '../../../components/RoleGate';
import useAuth from '../../../hooks/useAuth';
import { buildDropdownStateProps } from '../common/dropdownLoaderState.js';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;
const { TextArea } = Input;


// ─────────────────────────────────────────────────────────────────
// Constants — frozen mirrors of backend tdsTcs.constants.js.
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

// PAN — 5 letters + 4 digits + 1 letter (AAAPL1234C).
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// GSTIN — 15-char canonical shape.
// (validation kept light here; backend re-validates).

const PAN_STATUSES = Object.freeze([
  'AVAILABLE', 'NOT_AVAILABLE', 'INVALID'
]);

const SUPPLIER_TAX_TYPES = Object.freeze([
  'INDIVIDUAL', 'COMPANY', 'PARTNERSHIP', 'PROPRIETOR', 'BUSINESS', 'OTHER'
]);

const RESIDENT_STATUSES = Object.freeze([
  'RESIDENT', 'NON_RESIDENT', 'UNKNOWN'
]);

const PAN_STATUS_OPTIONS = PAN_STATUSES.map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));

const SUPPLIER_TAX_TYPE_OPTIONS = SUPPLIER_TAX_TYPES.map((v) => ({
  value: v,
  label: v.replace(/\b\w/g, (c) => c.toUpperCase())
}));

const RESIDENT_STATUS_OPTIONS = RESIDENT_STATUSES.map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));


// ─────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function fmtPct(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(4).replace(/\.?0+$/, '')}%`;
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// Certificate "valid now" derivation — both edges inclusive of today.
function isCertificateValidNow(row) {
  const validFrom = pickField(row, 'certificate_valid_from', 'certificateValidFrom');
  const validTo   = pickField(row, 'certificate_valid_to',   'certificateValidTo');
  if (!validFrom && !validTo) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (validFrom) {
    const f = new Date(validFrom);
    if (Number.isNaN(f.getTime()) || f > today) return false;
  }
  if (validTo) {
    const t = new Date(validTo);
    if (Number.isNaN(t.getTime()) || t < today) return false;
  }
  return true;
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function SupplierTaxProfilesPage() {
  const { me } = useAuth();
  const [form] = Form.useForm();

  // Form watches — drive conditional rendering for the certificate +
  // exempt panels. AntD's Form.useWatch keeps these reactive.
  const hasLowerCert = Form.useWatch('hasLowerDeductionCertificate', form);
  const isExempt     = Form.useWatch('isExempt', form);

  // Wildcard '*' is the master role.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm('erp.tds_tcs.profiles.view');
  const canUpdate = hasPerm('erp.tds_tcs.profiles.update');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput,   setSearchInput]   = useState('');
  const [search,        setSearch]        = useState('');
  const [panStatus,     setPanStatus]     = useState(null);
  const [residentStatus, setResidentStatus] = useState(null);
  const [exemptFilter,  setExemptFilter]  = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Modal state ──────────────────────────────────────────────
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Supplier dropdown state ──────────────────────────────────
  // `supplierError` drives the AntD Select notFoundContent via the
  // shared `buildDropdownStateProps` helper so a failed loader shows
  // "Failed to load — try refresh" instead of a silent empty dropdown
  // (Stage 6P-MED-FIX F-FD-7).
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierError,   setSupplierError]   = useState(null);

  // ── Supplier autocomplete loader ─────────────────────────────
  // Reuses the accounts/parties/suppliers lookup endpoint — tenant
  // aware + active-only — the same source the Supplier Payment form
  // uses (Stage 6P.7).
  const loadSuppliers = useCallback(async (q = '') => {
    setSupplierLoading(true);
    setSupplierError(null);
    try {
      const resp = await client.get('/api/erp/accounts/parties/suppliers', {
        params: { ...(q ? { q } : {}), limit: 100 }
      });
      const data = unwrap(resp);
      const list = Array.isArray(data) ? data
        : Array.isArray(data?.rows) ? data.rows
        : Array.isArray(data?.items) ? data.items
        : [];
      setSupplierOptions(list);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load suppliers.';
      setSupplierError(msg);
      setSupplierOptions([]);
    } finally {
      setSupplierLoading(false);
    }
  }, []);

  useEffect(() => {
    if (modalOpen) loadSuppliers('');
  }, [modalOpen, loadSuppliers]);

  // ── Debounced search wiring ──────────────────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (panStatus)      params.panStatus      = panStatus;
      if (residentStatus) params.residentStatus = residentStatus;
      if (exemptFilter === 'exempt')     params.isExempt = true;
      if (exemptFilter === 'not_exempt') params.isExempt = false;

      const resp = await client.get('/api/erp/tds-tcs/supplier-profiles', { params });
      const out  = unwrap(resp);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load supplier tax profiles.'),
        key: 'tt-profiles-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, panStatus, residentStatus, exemptFilter]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers ─────────────────────────────────────────
  const onPanStatus      = (v) => { setPanStatus(v || null);      setPage(1); };
  const onResidentStatus = (v) => { setResidentStatus(v || null); setPage(1); };
  const onExempt         = (v) => { setExemptFilter(v || null);   setPage(1); };
  const onReset = () => {
    setSearchInput('');
    setSearch('');
    setPanStatus(null);
    setResidentStatus(null);
    setExemptFilter(null);
    setPage(1);
  };

  // ── Modal open / close ───────────────────────────────────────
  const openAdd = useCallback(() => {
    setEditingRow(null);
    form.resetFields();
    form.setFieldsValue({
      supplierTaxType:               'OTHER',
      panStatus:                     'AVAILABLE',
      residentStatus:                'RESIDENT',
      hasLowerDeductionCertificate:  false,
      certificateRatePercent:        0,
      certificateLimitAmount:        0,
      certificateAmountUsed:         0,
      isExempt:                      false
    });
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    form.resetFields();
    const validFrom = pickField(row, 'certificate_valid_from', 'certificateValidFrom');
    const validTo   = pickField(row, 'certificate_valid_to',   'certificateValidTo');
    form.setFieldsValue({
      supplierId:                   pickField(row, 'supplier_id',    'supplierId')    || null,
      supplierName:                 pickField(row, 'supplier_name',  'supplierName')  || '',
      supplierGstin:                pickField(row, 'supplier_gstin', 'supplierGstin') || '',
      panNumber:                    pickField(row, 'pan_number',     'panNumber')     || '',
      panStatus:                    pickField(row, 'pan_status',     'panStatus')     || 'AVAILABLE',
      supplierTaxType:              pickField(row, 'supplier_tax_type', 'supplierTaxType') || 'OTHER',
      residentStatus:               pickField(row, 'resident_status',   'residentStatus')   || 'RESIDENT',
      hasLowerDeductionCertificate: !!pickField(row, 'has_lower_deduction_certificate', 'hasLowerDeductionCertificate'),
      certificateNo:                pickField(row, 'certificate_no',         'certificateNo')         || '',
      certificateSection:           pickField(row, 'certificate_section',    'certificateSection')    || '',
      certificateRatePercent:       Number(pickField(row, 'certificate_rate_percent',    'certificateRatePercent')    || 0),
      certificateLimitAmount:       Number(pickField(row, 'certificate_limit_amount',    'certificateLimitAmount')    || 0),
      certificateAmountUsed:        Number(pickField(row, 'certificate_amount_used',     'certificateAmountUsed')     || 0),
      certificateValidity:          (validFrom || validTo)
                                      ? [
                                          validFrom ? dayjs(validFrom) : null,
                                          validTo   ? dayjs(validTo)   : null
                                        ]
                                      : null,
      isExempt:                     !!pickField(row, 'is_exempt', 'isExempt'),
      exemptionReason:              pickField(row, 'exemption_reason', 'exemptionReason') || '',
      notes:                        pickField(row, 'notes', 'notes') || ''
    });
    setModalOpen(true);
  }, [form]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingRow(null);
    form.resetFields();
  }, [form]);

  // ── Auto-fill GSTIN + supplier_name when supplier changes ────
  const onSupplierChange = useCallback((supplierId) => {
    if (!supplierId) return;
    const match = supplierOptions.find((s) =>
      (s.id || s.supplierId) === supplierId
    );
    if (match) {
      form.setFieldsValue({
        supplierName:  match.supplierName || match.supplier_name || match.name || '',
        supplierGstin: match.gstin || match.supplierGstin || ''
      });
    }
  }, [form, supplierOptions]);

  // ── Submit handler ───────────────────────────────────────────
  const onSubmit = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_err) {
      return;
    }
    setSubmitting(true);
    try {
      const [validFrom, validTo] = Array.isArray(values.certificateValidity)
        ? values.certificateValidity
        : [null, null];

      const payload = {
        supplier_id:                       values.supplierId || null,
        supplier_name:                     values.supplierName,
        supplier_gstin:                    values.supplierGstin || null,
        pan_number:                        values.panNumber || null,
        pan_status:                        values.panStatus,
        supplier_tax_type:                 values.supplierTaxType,
        resident_status:                   values.residentStatus,
        has_lower_deduction_certificate:   !!values.hasLowerDeductionCertificate,
        certificate_no:                    values.hasLowerDeductionCertificate
                                             ? (values.certificateNo || null)
                                             : null,
        certificate_section:               values.hasLowerDeductionCertificate
                                             ? (values.certificateSection || null)
                                             : null,
        certificate_rate_percent:          values.hasLowerDeductionCertificate
                                             ? Number(values.certificateRatePercent) || 0
                                             : 0,
        certificate_limit_amount:          values.hasLowerDeductionCertificate
                                             ? Number(values.certificateLimitAmount) || 0
                                             : 0,
        certificate_amount_used:           Number(values.certificateAmountUsed) || 0,
        certificate_valid_from:            validFrom ? validFrom.format(ISO_FMT) : null,
        certificate_valid_to:              validTo   ? validTo.format(ISO_FMT)   : null,
        is_exempt:                         !!values.isExempt,
        exemption_reason:                  values.isExempt
                                             ? (values.exemptionReason || null)
                                             : null,
        notes:                             values.notes || null
      };

      if (editingRow) {
        const id = pickField(editingRow, 'id', 'id');
        await client.put(
          `/api/erp/tds-tcs/supplier-profiles/${encodeURIComponent(id)}`,
          payload
        );
        message.success('Supplier tax profile updated.');
      } else {
        await client.post('/api/erp/tds-tcs/supplier-profiles', payload);
        message.success('Supplier tax profile created.');
      }
      closeModal();
      await load();
    } catch (e) {
      message.error(errMessage(
        e,
        editingRow ? 'Failed to update profile.' : 'Failed to create profile.'
      ));
    } finally {
      setSubmitting(false);
    }
  }, [form, editingRow, closeModal, load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback(() => {
    const items = [];
    if (canUpdate) {
      items.push({ key: 'edit', icon: <EditOutlined />, label: 'Edit' });
    }
    return items;
  }, [canUpdate]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'edit': return openEdit(row);
      default:     return undefined;
    }
  }, [openEdit]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name = pickField(row, 'supplier_name', 'supplierName') || '—';
        return (
          <Tooltip title={name}>
            <span className="grn-supplier__name">{name}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'GSTIN',
      dataIndex: 'supplierGstin',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'supplier_gstin', 'supplierGstin');
        if (!v) return <span className="grn-muted">—</span>;
        return <Tag color="blue" className="grn-gstin-tag">{v}</Tag>;
      }
    },
    {
      title:     'PAN',
      dataIndex: 'panNumber',
      width:     130,
      render: (_v, row) => {
        const v = pickField(row, 'pan_number', 'panNumber');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'PAN Status',
      dataIndex: 'panStatus',
      width:     130,
      render: (_v, row) => {
        const v = pickField(row, 'pan_status', 'panStatus');
        if (!v) return <span className="grn-muted">—</span>;
        const color = v === 'AVAILABLE' ? 'success'
          : v === 'INVALID' ? 'error'
          : 'default';
        return (
          <Tag color={color} className="grn-status-tag">
            {v.replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Resident Status',
      dataIndex: 'residentStatus',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'resident_status', 'residentStatus');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {v.replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'TDS Applicable',
      dataIndex: 'tdsApplicable',
      width:     130,
      render: (_v, row) => {
        // Derived: a supplier is TDS-applicable when not exempt AND PAN
        // status is something other than "no rule".
        const exempt = !!pickField(row, 'is_exempt', 'isExempt');
        return (
          <Tag
            color={exempt ? 'default' : 'processing'}
            className="grn-status-tag"
          >
            {exempt ? 'Exempt' : 'Yes'}
          </Tag>
        );
      }
    },
    {
      title:     'TCS Applicable',
      dataIndex: 'tcsApplicable',
      width:     130,
      render: (_v, row) => {
        // Same gate — TCS rule selection runs at deduction time on the
        // backend; we surface the same yes/exempt shape here.
        const exempt = !!pickField(row, 'is_exempt', 'isExempt');
        return (
          <Tag
            color={exempt ? 'default' : 'processing'}
            className="grn-status-tag"
          >
            {exempt ? 'Exempt' : 'Yes'}
          </Tag>
        );
      }
    },
    {
      title:     'Certificate No',
      dataIndex: 'certificateNo',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'certificate_no', 'certificateNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Certificate Rate',
      dataIndex: 'certificateRatePercent',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtPct(pickField(row, 'certificate_rate_percent', 'certificateRatePercent'))}
        </span>
      )
    },
    {
      title:     'Certificate Validity',
      dataIndex: 'certificateValidity',
      width:     220,
      render: (_v, row) => {
        const from = pickField(row, 'certificate_valid_from', 'certificateValidFrom');
        const to   = pickField(row, 'certificate_valid_to',   'certificateValidTo');
        if (!from && !to) return <span className="grn-muted">—</span>;
        const validNow = isCertificateValidNow(row);
        return (
          <Space size={4}>
            <span className="grn-mono">{fmtDate(from)}</span>
            <span className="grn-muted">→</span>
            <span className="grn-mono">{fmtDate(to)}</span>
            <Tag
              color={validNow ? 'success' : 'warning'}
              className="grn-status-tag"
            >
              {validNow ? 'Valid' : 'Expired'}
            </Tag>
          </Space>
        );
      }
    },
    {
      title:     'Exempt',
      dataIndex: 'isExempt',
      width:     100,
      render: (_v, row) => {
        const exempt = !!pickField(row, 'is_exempt', 'isExempt');
        return (
          <Tag
            color={exempt ? 'warning' : 'default'}
            className="grn-status-tag"
          >
            {exempt ? 'Exempt' : 'No'}
          </Tag>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu();
        if (items.length === 0) return null;
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [buildRowMenu, onRowMenu]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view supplier tax profiles."
          description="Ask your master account to grant erp.tds_tcs.profiles.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><IdcardOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Supplier Tax Profiles</span>
            <span className="grn-headstrip__subtitle">
              Per-supplier PAN, residency, lower-deduction-certificate and
              exemption snapshot consulted by the TDS/TCS engine. The
              backend tracks certificate amount-used in real time —
              this surface only authors the snapshot.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
          <RoleGate>
            {canUpdate ? (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={openAdd}
              >
                Add Profile
              </Button>
            ) : null}
          </RoleGate>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search supplier / GSTIN / PAN / certificate…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All PAN statuses"
          value={panStatus || undefined}
          onChange={onPanStatus}
          options={PAN_STATUS_OPTIONS}
          style={{ width: 170 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All resident statuses"
          value={residentStatus || undefined}
          onChange={onResidentStatus}
          options={RESIDENT_STATUS_OPTIONS}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="Exempt filter"
          value={exemptFilter || undefined}
          onChange={onExempt}
          options={[
            { value: 'exempt',     label: 'Exempt only' },
            { value: 'not_exempt', label: 'Not exempt' }
          ]}
          style={{ width: 160 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'supplier_id', 'supplierId')
              || pickField(r, 'supplier_name', 'supplierName')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => {
                if (canUpdate) openEdit(row);
              }
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{ emptyText: loading ? ' ' : 'No supplier tax profiles yet.' }}
          />
        </Spin>
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        title={editingRow ? 'Edit Supplier Tax Profile' : 'Add Supplier Tax Profile'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={onSubmit}
        okText={editingRow ? 'Save Changes' : 'Create Profile'}
        confirmLoading={submitting}
        destroyOnClose
        width={780}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark="optional"
        >
          <Form.Item
            label="Supplier"
            name="supplierId"
            tooltip="Pick the existing supplier. Name + GSTIN auto-fill from the master."
          >
            <Select
              placeholder="Select supplier…"
              showSearch
              allowClear
              filterOption={false}
              onSearch={loadSuppliers}
              onChange={onSupplierChange}
              {...buildDropdownStateProps({
                loading: supplierLoading,
                error:   supplierError,
                data:    supplierOptions
              })}
              options={supplierOptions.map((s) => ({
                value: s.id || s.supplierId,
                label: (
                  <span>
                    {s.supplierCode ? (
                      <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>
                        {s.supplierCode}
                      </code>
                    ) : null}
                    {s.supplierName || s.supplier_name || s.name}
                    {s.gstin ? (
                      <Tag color="blue" style={{ marginLeft: 6 }}>{s.gstin}</Tag>
                    ) : null}
                  </span>
                )
              }))}
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              label="Supplier Name (snapshot)"
              name="supplierName"
              rules={[
                { required: true, message: 'Supplier name is required.' },
                { max: 220, message: 'Cannot exceed 220 characters.' }
              ]}
            >
              <Input maxLength={220} />
            </Form.Item>
            <Form.Item
              label="GSTIN"
              name="supplierGstin"
              rules={[
                {
                  pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                  message: 'GSTIN must be a valid 15-character GSTIN.'
                }
              ]}
            >
              <Input
                placeholder="15-char GSTIN"
                maxLength={15}
                onChange={(e) => form.setFieldsValue({
                  supplierGstin: String(e.target.value || '').toUpperCase()
                })}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Form.Item
              label="PAN"
              name="panNumber"
              rules={[
                {
                  pattern: PAN_REGEX,
                  message: 'PAN must match AAAPL1234C format.'
                }
              ]}
              tooltip="5 letters + 4 digits + 1 letter"
            >
              <Input
                placeholder="AAAPL1234C"
                maxLength={10}
                onChange={(e) => form.setFieldsValue({
                  panNumber: String(e.target.value || '').toUpperCase()
                })}
              />
            </Form.Item>
            <Form.Item
              label="PAN Status"
              name="panStatus"
              rules={[{ required: true, message: 'PAN status is required.' }]}
            >
              <Select options={PAN_STATUS_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Supplier Tax Type"
              name="supplierTaxType"
            >
              <Select options={SUPPLIER_TAX_TYPE_OPTIONS} />
            </Form.Item>
          </div>

          <Form.Item
            label="Resident Status"
            name="residentStatus"
          >
            <Select options={RESIDENT_STATUS_OPTIONS} />
          </Form.Item>

          <div
            className="rounded-md border border-surface-200 p-3 mb-3"
            style={{ background: '#fafbfc' }}
          >
            <Form.Item
              label="Has Lower Deduction Certificate"
              name="hasLowerDeductionCertificate"
              valuePropName="checked"
              style={{ marginBottom: hasLowerCert ? 12 : 0 }}
            >
              <Switch />
            </Form.Item>

            {hasLowerCert ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Form.Item
                    label="Certificate No"
                    name="certificateNo"
                    rules={[{ required: true, message: 'Certificate number is required.' }]}
                  >
                    <Input maxLength={60} />
                  </Form.Item>
                  <Form.Item
                    label="Certificate Section"
                    name="certificateSection"
                  >
                    <Input
                      maxLength={20}
                      placeholder="e.g. 194Q"
                      onChange={(e) => form.setFieldsValue({
                        certificateSection: String(e.target.value || '').toUpperCase()
                      })}
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Form.Item label="Cert. Rate %" name="certificateRatePercent">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      precision={4}
                      step={0.0001}
                      addonAfter="%"
                    />
                  </Form.Item>
                  <Form.Item label="Cert. Limit (₹)" name="certificateLimitAmount">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      step={1}
                    />
                  </Form.Item>
                  <Form.Item label="Cert. Used (₹)" name="certificateAmountUsed">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      step={1}
                      disabled
                    />
                  </Form.Item>
                </div>

                <Form.Item label="Certificate Validity" name="certificateValidity">
                  <RangePicker
                    style={{ width: '100%' }}
                    format={DATE_FMT}
                    allowEmpty={[true, true]}
                  />
                </Form.Item>
              </>
            ) : null}
          </div>

          <div
            className="rounded-md border border-surface-200 p-3 mb-3"
            style={{ background: '#fafbfc' }}
          >
            <Form.Item
              label="Tax Exempt"
              name="isExempt"
              valuePropName="checked"
              tooltip="When ON the deduction engine skips this supplier."
              style={{ marginBottom: isExempt ? 12 : 0 }}
            >
              <Switch />
            </Form.Item>
            {isExempt ? (
              <Form.Item
                label="Exemption Reason"
                name="exemptionReason"
                rules={[{ required: true, message: 'Reason is required when exempt.' }]}
              >
                <TextArea
                  rows={2}
                  maxLength={220}
                  placeholder="Short justification e.g. Government department, NIL TDS notification…"
                />
              </Form.Item>
            ) : null}
          </div>

          <Form.Item label="Notes" name="notes">
            <TextArea rows={3} maxLength={4000} placeholder="Optional internal notes." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
